/**
 * "Modifica sul sito": quando si può aprire la pagina pubblicata in modifica
 * e che cosa dire quando non si può (D-19, document/26 §7).
 *
 * La decisione è pura: dato lo stato del sito e della pagina, dice se l'indirizzo
 * è pronto o perché non lo è. Lo schermo sceglie solo come mostrarlo.
 *
 * Il segno `#cms-edit` è l'unico modo in cui la UI accende l'editor sul sito:
 * non porta con sé nessun permesso, dice solo «in questa scheda voglio
 * modificare». Chi decide è il gateway, quando il ponte chiede di entrare.
 */

/** Il frammento che il piccolo stub del sito riconosce. Lo stesso di `IN_PLACE_HASH` in site-kit. */
export const EDIT_HASH = '#cms-edit'

export interface InPlaceSettings {
  enabled: boolean
  /** Vuoto: tutti i domini registrati. */
  allowedHosts: string[]
  registeredHosts: string[]
  effectiveHosts: string[]
}

export type EditOnSite =
  | { kind: 'ready'; url: string }
  | { kind: 'loading' }
  /** Il sito non ha acceso l'opzione: si accende nelle impostazioni, poi si ripubblica. */
  | { kind: 'off' }
  /** La pagina o il contenuto non sono ancora online: non c'è un indirizzo pubblico da aprire. */
  | { kind: 'not-online' }
  /** Il contenuto non ha un indirizzo noto (un modello con campi che l'elenco non porta). */
  | { kind: 'no-address' }

export function editOnSite(input: {
  settings: InPlaceSettings | null
  /** L'indirizzo pubblico, con una sola `/` iniziale; `null` se non si può calcolare. */
  path: string | null
  online: boolean
  primaryDomain: string | null | undefined
}): EditOnSite {
  if (!input.settings) return { kind: 'loading' }
  if (!input.settings.enabled) return { kind: 'off' }
  if (!input.online) return { kind: 'not-online' }
  if (input.path === null) return { kind: 'no-address' }
  // Se il proprietario ha ristretto i domini e il principale non c'è, si apre il primo ammesso.
  const hosts = input.settings.effectiveHosts
  const primary = (input.primaryDomain ?? '').replace(/^https?:\/\//i, '').replace(/\/+$/, '').toLowerCase()
  if (!primary) return { kind: 'no-address' }
  const host = hosts.length === 0 || hosts.includes(primary) ? primary : hosts[0]
  if (!host || /[\s/]/.test(host)) return { kind: 'no-address' }
  const path = input.path.startsWith('/') ? input.path : `/${input.path}`
  return { kind: 'ready', url: `https://${host}${path}${EDIT_HASH}` }
}
